import React, { useCallback, useEffect, useState } from "react";
import { Search, Trash2, Shield, BookOpen } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function ModulesDialog({ user, modules, onClose, onConfirm }) {
  const [picked, setPicked] = useState(() => {
    const req = user.requested_modules || [];
    return Array.from(new Set([...(user.assigned_modules || []), ...req]));
  });
  const toggle = (s) => setPicked((p) => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const allSlugs = modules.map((m) => m.slug);
  const allSelected = allSlugs.every((s) => picked.includes(s));
  const selectAll = () => setPicked(allSelected ? [] : allSlugs);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white border border-border max-w-md w-full p-6 max-h-[90vh] flex flex-col">
        <div className="overline mb-2">Edit modules</div>
        <div className="font-display font-black text-xl tracking-tight mb-1">{user.full_name}</div>
        <div className="text-[13px] text-muted-foreground mb-4">{user.email}</div>
        {user.requested_modules?.length > 0 && (
          <div className="mb-5 p-3 border border-primary/30 bg-primary/5 text-[12.5px]">
            <div className="overline text-primary mb-1">Also requesting</div>
            <div className="font-semibold">
              {user.requested_modules.map((s) => modules.find((m) => m.slug === s)?.name || s).join(", ")}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Assigned modules</div>
          <button type="button" onClick={selectAll} data-testid="edit-modules-select-all"
                  className="text-[11.5px] font-semibold text-primary link-underline">
            {allSelected ? "Clear all" : "Select all"}
          </button>
        </div>
        <div className="space-y-2 mb-6 overflow-y-auto pr-1">
          {modules.map((m) => {
            const isReq = user.requested_modules?.includes(m.slug);
            return (
              <label key={m.slug} className={`flex items-center gap-3 p-3 border cursor-pointer ${picked.includes(m.slug) ? "border-primary bg-primary/5" : "border-border"}`}>
                <input type="checkbox" checked={picked.includes(m.slug)} onChange={() => toggle(m.slug)} className="accent-[#002FA7]" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13.5px] truncate">{m.name}</div>
                  <div className="text-[11.5px] text-muted-foreground font-mono">{m.slug}</div>
                </div>
                {isReq && <span className="sharp-badge text-primary border-primary/40 shrink-0">Requested</span>}
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} className="h-10 px-5 border border-border font-semibold text-[13px]">Cancel</button>
          <button data-testid="edit-modules-save" onClick={() => onConfirm(picked)} className="h-10 px-5 bg-primary text-primary-foreground font-semibold text-[13px]">Save</button>
        </div>
      </div>
    </div>
  );
}

function RoleDialog({ user, onClose, onConfirm }) {
  const [role, setRole] = useState(user.role);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white border border-border max-w-md w-full p-6">
        <div className="overline mb-2">Change role</div>
        <div className="font-display font-black text-xl tracking-tight mb-1">{user.full_name}</div>
        <div className="text-[13px] text-muted-foreground mb-6">{user.email}</div>
        <div className="space-y-2 mb-6">
          {["user", "admin", "super_admin"].map((r) => (
            <label key={r} className={`flex items-center gap-3 p-3 border cursor-pointer ${role === r ? "border-primary bg-primary/5" : "border-border"}`}>
              <input type="radio" name="role" checked={role === r} onChange={() => setRole(r)} className="accent-[#002FA7]" />
              <div className="font-semibold text-[13.5px] capitalize">{r.replace("_", " ")}</div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-5 border border-border font-semibold text-[13px]">Cancel</button>
          <button data-testid="role-save" onClick={() => onConfirm(role)} className="h-10 px-5 bg-primary text-primary-foreground font-semibold text-[13px]">Update role</button>
        </div>
      </div>
    </div>
  );
}

export default function AllUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [roleEdit, setRoleEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const [u, m] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/modules"),
      ]);
      setUsers(u.data);
      setModules(m.data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const saveModules = async (slugs) => {
    try {
      await api.put(`/admin/users/${editing.user_id}/modules`, { assigned_modules: slugs });
      setEditing(null); load();
    } catch (e) { setError(errorMessage(e)); }
  };
  const saveRole = async (role) => {
    try {
      await api.put(`/admin/users/${roleEdit.user_id}/role`, { role });
      setRoleEdit(null); load();
    } catch (e) { setError(errorMessage(e)); }
  };
  const delUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      load();
    } catch (e) { setError(errorMessage(e)); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: "border-amber-500/40 bg-amber-500/10 text-amber-700",
      active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700",
      rejected: "border-destructive/40 bg-destructive/5 text-destructive",
    };
    return <span className={`sharp-badge ${map[s] || ""}`}>{s}</span>;
  };

  return (
    <AppShell section="admin">
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="overline mb-3">Admin Console</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-8">All users</h1>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="users-search-input"
              placeholder="Search by name, email, organization..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-11 pl-10 pr-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-[13.5px]"
            />
          </div>
          <select data-testid="users-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-11 px-3 border border-border bg-white text-[13.5px] focus:outline-none focus:border-primary">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {error && <div className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">{error}</div>}

        <div className="border border-border bg-white overflow-x-auto">
          {loading ? (
            <div className="p-8 overline">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-[13px]">No users match the current filter.</div>
          ) : (
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-accent/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Role</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-left p-3 font-semibold">Modules</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="p-3 font-semibold">{u.full_name}</td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3"><span className="sharp-badge capitalize">{(u.role || "").replace("_", " ")}</span></td>
                    <td className="p-3">{statusBadge(u.status)}</td>
                    <td className="p-3 text-muted-foreground">
                      {(u.assigned_modules || []).join(", ") || "-"}
                      {u.requested_modules?.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary font-semibold">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary inline-block" />
                          Requesting: {u.requested_modules.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-2">
                        <button title="Edit modules" onClick={() => setEditing(u)} data-testid={`edit-modules-${u.email}`}
                          className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-[12px] font-semibold hover:bg-accent">
                          <BookOpen className="h-3.5 w-3.5" /> Modules
                        </button>
                        {me?.role === "super_admin" && (
                          <>
                            <button title="Change role" onClick={() => setRoleEdit(u)} data-testid={`edit-role-${u.email}`}
                              className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-[12px] font-semibold hover:bg-accent">
                              <Shield className="h-3.5 w-3.5" /> Role
                            </button>
                            <button title="Delete" onClick={() => delUser(u)} data-testid={`delete-user-${u.email}`}
                              className="h-9 px-3 inline-flex items-center gap-1.5 border border-destructive/30 text-destructive text-[12px] font-semibold hover:bg-destructive/5">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {editing && <ModulesDialog user={editing} modules={modules} onClose={() => setEditing(null)} onConfirm={saveModules} />}
      {roleEdit && <RoleDialog user={roleEdit} onClose={() => setRoleEdit(null)} onConfirm={saveRole} />}
    </AppShell>
  );
}
